import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { LinkedRoom, SectionSeeds } from '../../types';

/**
 * Take search terms from the boardroom feeding this subject.
 *
 * The one place room content is COPIED rather than read live, and the reason is
 * that a keyword is not knowledge: it grows its own SERPs, metrics and pages,
 * and those belong to the node from the moment it exists. Unlinking the room
 * must not orphan them.
 *
 * So this is a deliberate act with a preview, not a sync. You pick a section,
 * see exactly what it would add, untick what you do not want, and apply.
 */

interface Props {
  projectId: number;
  topicId: number;
  topicName: string;
  onClose: () => void;
  onSeeded: () => void;
}

export default function SeedFromRoom({
  projectId,
  topicId,
  topicName,
  onClose,
  onSeeded,
}: Props) {
  const [room, setRoom] = useState<LinkedRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState<string | null>(null);
  const [seeds, setSeeds] = useState<SectionSeeds | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    api
      .getRoomForSubject(projectId)
      .then(setRoom)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the room'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const pick = useCallback(
    async (kind: string) => {
      setSection(kind);
      setSeeds(null);
      setNotice('');
      try {
        const preview = await api.getSectionSeeds(room!.boardroom_id, kind);
        setSeeds(preview);
        // Everything ticked to start: you opened this to take the section, and
        // unticking the few you do not want is less work than ticking the
        // fifty you do.
        setChosen(new Set(preview.terms));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not read that section');
      }
    },
    [room],
  );

  const apply = async () => {
    if (!room || !section || !chosen.size) return;
    setBusy(true);
    try {
      const result = await api.seedFromSection(room.boardroom_id, section, {
        topic_id: topicId,
        terms: Array.from(chosen),
      });
      setNotice(result.message);
      onSeeded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add those keywords');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/70" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Take keywords from {room ? `“${room.name}”` : 'the linked room'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Adds them to <span className="font-medium">{topicName}</span> as keywords
            to research. Sections not listed here hold reference material rather than
            search terms - the writing agent reads those directly.
          </p>
        </div>

        {error && (
          <div className="mx-5 mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div className="mx-5 mt-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-3 py-2 rounded text-sm">
            {notice}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          ) : !room ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No recon is linked to this subject yet. Link one from the
              subject's header to pull from it.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5">
                {room.sections
                  .filter((s) => SEEDABLE.has(s.kind) && s.count > 0)
                  .map((s) => (
                    <button
                      key={s.kind}
                      onClick={() => pick(s.kind)}
                      className={`px-2.5 py-1 text-xs rounded border ${
                        section === s.kind
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      title={s.gives}
                    >
                      {s.label}
                      <span className="ml-1.5 opacity-70 tabular-nums">{s.count}</span>
                    </button>
                  ))}
              </div>

              {seeds && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {chosen.size} of {seeds.total} selected
                    </p>
                    <div className="flex gap-2 text-xs">
                      <button
                        onClick={() => setChosen(new Set(seeds.terms))}
                        className="text-blue-700 dark:text-blue-300 hover:underline"
                      >
                        All
                      </button>
                      <button
                        onClick={() => setChosen(new Set())}
                        className="text-gray-500 dark:text-gray-400 hover:underline"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {seeds.terms.map((term) => {
                      const on = chosen.has(term);
                      return (
                        <button
                          key={term}
                          onClick={() =>
                            setChosen((prev) => {
                              const next = new Set(prev);
                              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                              on ? next.delete(term) : next.add(term);
                              return next;
                            })
                          }
                          className={`px-2 py-0.5 text-xs rounded-full border ${
                            on
                              ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200'
                              : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 line-through'
                          }`}
                        >
                          {term}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
          <button
            onClick={apply}
            disabled={busy || !chosen.size}
            className="px-4 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Adding…' : `Add ${chosen.size}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Mirrors `SEEDABLE` on the server. The others hold reference material a
 *  writer reads, not terms anyone would search for — offering them here would
 *  invite keywords made out of prose. */
const SEEDABLE = new Set([
  'demand',
  'competitors',
  'pains',
  'facets',
  'solution_types',
  'features',
]);
